import React, { useContext } from "react";
import {
    ButtonProps as BaseButtonProps,
    LinkProps as BaseLinkProps,
    Button,
    ButtonContext,
    Link,
    LinkContext,
    VisuallyHidden,
    useSlottedContext,
} from "react-aria-components";

import { CharBlockOrNull, Context } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";
import { slugify } from "@thelabnyc/thelabui/src/utils/text";

import { Separator } from "../Separator";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

type ButtonProps = Omit<BaseButtonProps, "children" | "className">;
type LinkProps = Omit<BaseLinkProps, "children" | "className">;

export interface SimpleLinkBlock {
    display_name?: CharBlockOrNull;
    link_type?: null | ("page" | "external" | "email" | "document");
    anchor?: CharBlockOrNull;
    url: null | string;
}
export type ClickableProps = React.PropsWithChildren<
    (ButtonProps | LinkProps) &
        React.RefAttributes<HTMLButtonElement | HTMLAnchorElement> & {
            simpleLink?: SimpleLinkBlock;
            className?: string;
        }
>;

const isLink = (props: ClickableProps): props is LinkProps => {
    return typeof (props as LinkProps).href === "string";
};

const simpleLinkBlock = (props: SimpleLinkBlock) => {
    const target = props.link_type === "external" ? "_blank" : "_self";
    const href = props.url ?? undefined;

    return {
        target,
        href,
        children: props.display_name,
    };
};
// new URL(input, base) handles both absolute and relative inputs (base is
// ignored when input is absolute). Wrapped in try/catch since it throws on
// invalid input; URL.parse would be cleaner but isn't supported in Safari < 17.
const tryParseURL = (input: string, base?: string): URL | null => {
    try {
        return new URL(input, base);
    } catch {
        return null;
    }
};

const defaultTrackLabel = (simpleLink: SimpleLinkBlock): string | undefined => {
    if (simpleLink.display_name) return slugify(simpleLink.display_name);
    if (simpleLink.url) return slugify(simpleLink.url);
    return undefined;
};

export const Clickable = ({ simpleLink, ...props }: ClickableProps) => {
    const buttonContext = useSlottedContext(ButtonContext, props.slot);
    const linkContext = useSlottedContext(LinkContext, props.slot);
    const reactivatedContext = useContext(Context);
    const trackLabel = simpleLink ? defaultTrackLabel(simpleLink) : undefined;
    if (simpleLink) props = { ...simpleLinkBlock(simpleLink), ...props };

    if ("href" in props && props.href) {
        const url = tryParseURL(props.href, reactivatedContext.request.url);
        const here = new URL(reactivatedContext.request.url);
        if (url && url.protocol.startsWith("http") && url.host !== here.host) {
            // Preserve referrer for links to sibling subdomains (e.g. leanin.org
            // -> connect.leanin.org) so cross-property analytics attribution works.
            // Note: the last-two-labels shortcut is only correct for single-label
            // TLDs like .org/.com. Two-label public suffixes (.co.uk, .com.au)
            // would be misclassified as same-site — fine today, revisit if we
            // start linking to partner sites on those TLDs.
            const registrableDomain = (h: string) =>
                h.split(".").slice(-2).join(".");
            const isSameSite =
                registrableDomain(url.host) === registrableDomain(here.host);
            // Same-site links also override the site-wide Referrer-Policy
            // (SECURE_REFERRER_POLICY = strict-origin-when-cross-origin), which
            // would otherwise trim the referrer to the bare origin and hide which
            // page the click came from. no-referrer-when-downgrade sends the full
            // URL except on an https -> http downgrade.
            props = isSameSite
                ? {
                      ...props,
                      target: "_blank",
                      rel: "noopener",
                      referrerPolicy: "no-referrer-when-downgrade",
                  }
                : {
                      ...props,
                      target: "_blank",
                      rel: "noopener noreferrer",
                  };
        }
    }

    if (isLink(props))
        return (
            // @ts-expect-error I give up - I don't need ref, I just need it passable but I don't know how to do that. This works. Promise
            <Link
                data-track-label={trackLabel}
                {...linkContext}
                {...props}
                className={props.className ?? styles.base}
            />
        );
    else
        return (
            // @ts-expect-error I give up - I don't need ref, I just need it passable but I don't know how to do that. This works. Promise
            <Button
                data-track-label={trackLabel}
                {...buttonContext}
                {...props}
                className={props.className ?? styles.base}
            />
        );
};

export const ButtonPrimary = (props: ClickableProps) => (
    <Clickable
        {...props}
        className={concatClassNames([props.className, styles.buttonPrimary])}
    />
);
export const ButtonSecondary = (props: ClickableProps) => (
    <Clickable
        {...props}
        className={concatClassNames([props.className, styles.buttonSecondary])}
    />
);

export const ButtonTertiary = (props: ClickableProps) => (
    <Clickable
        {...props}
        className={concatClassNames([props.className, styles.buttonTertiary])}
    />
);
export const ButtonTextPrimary = (props: ClickableProps) => (
    <Clickable
        {...props}
        className={concatClassNames([
            props.className,
            styles.buttonTextPrimary,
        ])}
    />
);

export const ButtonTextSecondary = (props: ClickableProps) => (
    <Clickable
        {...props}
        className={concatClassNames([
            props.className,
            styles.buttonTextSecondary,
        ])}
    />
);

export const TextLink = (props: ClickableProps) => (
    <Clickable
        {...props}
        className={concatClassNames([props.className, styles.textLink])}
    />
);

export const IconButton = ({
    icon,
    variant = "outline",
    className,
    label,
    ...props
}: ClickableProps & {
    icon: React.ComponentProps<typeof Svg>["name"];
    /**
     * Put a brief description of what the button does
     */
    label?: string;
    variant?: "outline" | "filled";
    children?: never;
}) => (
    <Clickable
        {...props}
        className={concatClassNames([
            className,
            variant === "outline"
                ? styles.iconButtonOutline
                : styles.iconButtonFilled,
        ])}
    >
        <VisuallyHidden>{label}</VisuallyHidden>
        <Svg name={icon} aria-hidden="true" />
    </Clickable>
);

export const LongButton = ({
    icon = "arrow",
    variant,
    className,
    children,
    ...props
}: ClickableProps & {
    icon?: React.ComponentProps<typeof Svg>["name"];
    variant: "primary" | "secondary";
}) => (
    <Clickable
        {...props}
        className={concatClassNames([
            className,
            variant === "secondary"
                ? styles.longButtonSecondary
                : styles.longButtonPrimary,
        ])}
    >
        <Separator />
        <span>{children ?? props.simpleLink?.display_name}</span>
        <Svg name={icon} aria-hidden="true" />
    </Clickable>
);
